import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "v-account" }
const _hoisted_2 = { class: "body" }
const _hoisted_3 = { class: "account" }
const _hoisted_4 = { class: "label" }
const _hoisted_5 = {
  key: 0,
  class: "activity"
}
const _hoisted_6 = { class: "actions" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 0,
  class: "code"
}
const _hoisted_9 = { class: "code__main" }
const _hoisted_10 = { class: "code__main--time" }
const _hoisted_11 = { class: "time" }
const _hoisted_12 = { class: "modal__footer" }

import Filter from "@/features/account/ui/filter.vue";
import {useAccountStore} from "@/features/account/account.store";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import router from "@/router";
import {useAppStore} from "@/store/app.store";
import {useAlert} from "@/utils/alert";
import {IAccount, IAccountStart} from "@/features/account/account.types";
import {RefreshCcw04, Eye, EyeOff, Pencil01} from "utruckin-icons";
import {DateTime} from "luxon";


export default /*@__PURE__*/_defineComponent({
  __name: 'AccountView',
  setup(__props) {

const accountStore = useAccountStore()
const appStore = useAppStore();
const {show} = useAlert()

const list = ref<IAccount[]>([]);

const loading = ref<boolean>(true);
const editingModal = ref<boolean>(false);
const modalTitle = ref<string>("");
const modalItem = ref<IAccountStart>({
  session_name: "",
  phone_number: "",
  two_step_password: "",
});
const showError = ref<boolean>(false);
const isReconnecting = ref<boolean>(false);
const reconnectingAccountId = ref<string>("");

const codeIsAvailable = ref<boolean>(false);
const passwordType = ref<'text' | 'password'>("password");
const smsCode = ref<string>("");
const timeLeft = ref<number>(180);
let timer: ReturnType<typeof setInterval> | null = null

const formattedTime = computed(() => {
  const minutes = Math.floor(timeLeft.value / 60)
  const seconds = timeLeft.value % 60

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

const startTimer = () => {
  timer = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--
    } else {
      stopTimer()
    }
  }, 1000)
}

const stopTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const handleRowClicked = (row: any, rowIndex: number) => {
  router.push(`/message/${row.id}`);
}

const getList = async () => {
  list.value = await accountStore.getAccounts(loading);
}

const edit = async (item?: IAccountStart) => {
  editingModal.value = true;
  if (item) {
    modalTitle.value = 'Edit Account';
    modalItem.value = item
  } else {
    modalTitle.value = 'Add Account';
  }
}

const closeModal = () => {
  modalTitle.value = "";
  passwordType.value = 'password'
  codeIsAvailable.value = false
  isReconnecting.value = false
  reconnectingAccountId.value = "";
  stopTimer()
  modalItem.value = {
    session_name: "",
    phone_number: "",
    two_step_password: "",
  }

  showError.value = false;
}

const cancelEditButton = () => {
  editingModal.value = false;
  closeModal()
}

const saveEditButton = async () => {
  if (loading.value) {
    return;
  }
  if (!modalItem.value.session_name || !modalItem.value.phone_number) {
    showError.value = true;
    return
  }

  let request: IAccountStart = {...modalItem.value}

  request.phone_number = request.phone_number.split('+')[1];

  if (isReconnecting.value) {
    await accountStore.reconnectAccount(loading, reconnectingAccountId.value, request, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        codeIsAvailable.value = true;
        startTimer()
      }
    })
  } else {
    await accountStore.startAccount(loading, request, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        codeIsAvailable.value = true;
        startTimer()
      }
    })
  }
}

const confirmAccount = async () => {
  if (loading.value) {
    return;
  }

  let result = {
    session_name: modalItem.value.session_name,
    code: smsCode.value,
  }

  if (isReconnecting.value) {
    await accountStore.confirmReconnect(loading, reconnectingAccountId.value, result, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        cancelEditButton()
        appStore.triggerRefresh()
      }
    })
  } else {
    await accountStore.confirmAccount(loading, result, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        cancelEditButton()
        appStore.triggerRefresh()
      }
    })
  }
}

const handleAppendClickPassword = () => {
  passwordType.value = passwordType.value === "password" ? "text" : "password";
}

const goBackButton = () => {
  stopTimer()
  codeIsAvailable.value = false
}

const formatText = (label: string) => {
  return label.split("_").join(" ")
}

const reconnect = (item: any, e: any) => {
  e.stopPropagation()
  edit(item);
  modalItem.value.phone_number = "+" + item.phone;
  modalItem.value.session_name = item.session_name;
  isReconnecting.value = true;
  reconnectingAccountId.value = item.id;
}

const editItem = (item: any, e: any) => {
  e.stopPropagation()
  edit(item);
  modalItem.value.phone_number = "+" + item.phone;
  modalItem.value.session_name = item.session_name;
}

const utcIsoToZoneRelative = (value: string) => DateTime.fromISO(value, {zone: "utc"}).setZone().toRelative()!

onMounted(() => {
  getList()
})

onUnmounted(() => {
  stopTimer()
})

watch(
    () => appStore.refreshKey,
    () => {
      getList()
    },
    {
      immediate: false,
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(Filter, {
        onClickedAdd: edit,
        store: _unref(accountStore)
      }, null, 8, ["store"]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(CommonTable, {
          items: list.value,
          loading: loading.value,
          headers: _unref(accountStore).headers,
          onRowClicked: handleRowClicked
        }, {
          id: _withCtx(({index}) => [
            _createTextVNode(_toDisplayString(index + 1), 1)
          ]),
          account: _withCtx(({item}) => [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("span", null, _toDisplayString(item.session_name), 1),
              _createElementVNode("span", {
                class: _normalizeClass(["status", item.health.runtime_status == 'disconnected' ? 'disconnected' : 'connected'])
              }, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(formatText(item.health.auth_status)), 1)
              ], 2),
              (item.health.runtime_status == 'disconnected')
                ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(utcIsoToZoneRelative(item.health.last_heartbeat_at)), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          all: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.messages.total), 1)
          ]),
          text: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.messages.text), 1)
          ]),
          voice: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.messages.voice + item.messages.document + item.messages.audio), 1)
          ]),
          call: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.messages.calls), 1)
          ]),
          photo: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.messages.photo + item.messages.video + item.messages.round), 1)
          ]),
          all_errors: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.errors.total), 1)
          ]),
          reply_time: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.errors.reply_time_exceed), 1)
          ]),
          unanswered: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.errors.missed_calls + item.errors.declined_calls), 1)
          ]),
          deleted: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.errors.deleted), 1)
          ]),
          edited: _withCtx(({item}) => [
            _createTextVNode(_toDisplayString(item.errors.edited), 1)
          ]),
          actions: _withCtx(({item}) => [
            _createElementVNode("div", _hoisted_6, [
              (item.health.runtime_status == 'disconnected')
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "reconnect",
                    onClick: (e) => {reconnect(item, e)}
                  }, [
                    _createVNode(_unref(RefreshCcw04), { size: 16 })
                  ], 8, _hoisted_7))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["items", "loading", "headers"])
      ])
    ]),
    _createVNode(CommonModal, {
      modelValue: editingModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editingModal).value = $event)),
      title: modalTitle.value,
      onClose: closeModal
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_12, [
          _createVNode(CommonButton, {
            type: "secondary",
            label: "Cancel",
            onClicked: cancelEditButton
          }),
          (codeIsAvailable.value)
            ? (_openBlock(), _createBlock(CommonButton, {
                key: 0,
                type: "secondary",
                label: "Back",
                onClicked: goBackButton
              }))
            : _createCommentVNode("", true),
          (!codeIsAvailable.value)
            ? (_openBlock(), _createBlock(CommonButton, {
                key: 1,
                type: "primary",
                label: "Next",
                loading: loading.value,
                onClicked: saveEditButton
              }, null, 8, ["loading"]))
            : (_openBlock(), _createBlock(CommonButton, {
                key: 2,
                type: "primary",
                label: "Save",
                loading: loading.value,
                onClicked: confirmAccount
              }, null, 8, ["loading"]))
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createVNode(CommonInput, {
            label: "Name",
            placeholder: "Name",
            "model-value": modalItem.value.session_name,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modalItem.value.session_name) = $event)),
            disabled: codeIsAvailable.value || loading.value || isReconnecting.value,
            error: !modalItem.value.session_name && showError.value,
            errorMessage: "Value is required"
          }, null, 8, ["model-value", "disabled", "error"]),
          _createVNode(CommonInput, {
            label: "Phone number",
            placeholder: "Phone number",
            "model-value": modalItem.value.phone_number,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((modalItem.value.phone_number) = $event)),
            disabled: codeIsAvailable.value || loading.value || isReconnecting.value,
            error: !modalItem.value.phone_number && showError.value,
            mask: "+###############",
            errorMessage: "Value is required"
          }, null, 8, ["model-value", "disabled", "error"]),
          _createVNode(CommonInput, {
            label: "Password",
            placeholder: "Password",
            type: passwordType.value,
            "append-icon": passwordType.value == 'password' ? _unref(Eye) : _unref(EyeOff),
            onAppendIconClick: handleAppendClickPassword,
            "model-value": modalItem.value.two_step_password,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((modalItem.value.two_step_password) = $event)),
            disabled: codeIsAvailable.value || loading.value
          }, null, 8, ["type", "append-icon", "model-value", "disabled"])
        ]),
        (codeIsAvailable.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(CommonInput, {
                label: "Sms code",
                placeholder: "Sms code",
                "model-value": smsCode.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((smsCode).value = $event)),
                disabled: loading.value,
                mask: "######"
              }, null, 8, ["model-value", "disabled"]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _cache[5] || (_cache[5] = _createElementVNode("span", { class: "label" }, "Send code again after", -1)),
                  _createElementVNode("span", _hoisted_11, _toDisplayString(formattedTime.value), 1)
                ]),
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "code__main--action" }, " Resend code ", -1))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})